import { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import {
  BulkEditListingGroupSettingsDialog,
  BulkEditListingGroupSettingsDialogProps,
} from 'src/dialogs/BulkEdits/BulkEditListingGroupSettingsDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { BulkEditListingGroupSettingsFields } from 'src/modals/GroupListings/components/groupingTypes';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingGroupClient,
  BulkEditListingGroupSettingsInput,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  EventWithData,
  ListingGroupUndercutSetting,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_LISTING_GROUP_SETTINGS_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchBulkEditListingGroupSettings } from './LaunchBulkEditListingGroupSettings';

export const useBulkEditListingGroupSettings = (
  filterQueryWithEventIds: ListingQuery,
  eventWithData: EventWithData,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const updateKey = INVENTORY_BULK_UPDATE_LISTING_GROUP_SETTINGS_UPDATE_KEY;

  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const eventCurrency = useMemo(
    () => eventWithData.entities.listings?.[0]?.currency ?? 'USD',
    [eventWithData.entities.listings]
  );

  const selectedListingIds = useMemo(
    () => filterQueryWithEventIds.entityIds ?? [],
    [filterQueryWithEventIds.entityIds]
  );

  const onlyListingGroupsSelected = useMemo(() => {
    if (
      selectedListingIds.length === 0 ||
      eventWithData.entities.listings == null
    ) {
      return true;
    }
    const selectedListings =
      eventWithData.entities.listings
        ?.flatMap(flattenListingGroup)
        .filter((l) => selectedListingIds.includes(l.id)) ?? [];

    const selectedListingGroups = eventWithData.entities.listings.filter((l) =>
      selectedListingIds.includes(l.id)
    );

    if (!selectedListingGroups || selectedListingGroups.length === 0) {
      return false;
    }

    const selectedListingGroupIds = selectedListingGroups
      .map((l) => l.ltGrpId)
      .filter((id) => id != null);

    return (selectedListings ?? []).every((l) =>
      selectedListingGroupIds.includes(l.ltGrpId ?? '')
    );
  }, [selectedListingIds, eventWithData.entities.listings]);

  const event = eventWithData.event;

  const { openDialog: openBulkEditDialog, closeDialog: closeBulkEditDialog } =
    useDialog<BulkEditListingGroupSettingsDialogProps>(
      DialogId.BulkUpdateListingGroupSettings,
      BulkEditListingGroupSettingsDialog
    );

  const onSubmit = useCallback(
    (
      input: BulkEditListingGroupSettingsFields | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        tryInvokeApi(
          async () => {
            const client = await new BulkEditListingGroupClient(
              activeAccountWebClientConfig
            );
            const preview = await client.bulkEditListingsGroupSettingsPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkEditListingGroupSettingsPreview', error, {
              trackErrorData: {
                filterQueryWithEventIds,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (input) {
        tryInvokeApi(
          async () => {
            const client = await new BulkEditListingGroupClient(
              activeAccountWebClientConfig
            );

            const rankPremiums: ListingGroupUndercutSetting = {
              undAbsAmt: input.rankPremiums?.undercutAbsoluteAmount ?? null,
              undRelAmt: input.rankPremiums?.undercutRelativeAmount ?? null,
              actRankUndAbsAmt: null,
              actRankUndRelAmt: null,
            };

            const listingGroupListingIds = preview!.listings
              ?.flatMap(flattenListingGroup)
              .filter(({ id, isLtGrp }) => id > 0 && !isLtGrp)
              .map((l) => l.id);

            const payload: BulkEditListingGroupSettingsInput = {
              desiredActiveListings: input.desiredActiveListings ?? null,
              deprioritizedQuantities: input.deprioritizedQuantities ?? null,
              groupUndercutSetting: rankPremiums,
              marketplaceSettings: input.marketplaceSettings ?? null,
              autoPricingInputs: input.pricingSettingsInputs ?? null,
              listingGroupListingIds: listingGroupListingIds ?? [],
              viagogoEventId: event?.viagId ?? null,
            };

            const succeeded = await client.bulkEditListingGroupSettings(
              payload,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                await refreshExpandedListItems();
              }

              closeBulkEditDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkEditListingGroupSettings', error, {
              trackErrorData: {
                input,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditDialog,
      event?.viagId,
      filterQueryWithEventIds,
      refreshExpandedListItems,
      setIsLoading,
      showErrorDialog,
      updateKey,
    ]
  );

  const openDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      openBulkEditDialog({
        event: event,
        currency: eventCurrency,
        updateKey: INVENTORY_BULK_UPDATE_LISTING_GROUP_SETTINGS_UPDATE_KEY,
        isLoading: isLoading,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditDialog,
      });
    },
    [
      closeBulkEditDialog,
      event,
      eventCurrency,
      isLoading,
      onSubmit,
      openBulkEditDialog,
      setIsLoading,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditDialog({
      event: event,
      currency: eventCurrency,
      updateKey: INVENTORY_BULK_UPDATE_LISTING_GROUP_SETTINGS_UPDATE_KEY,
      isLoading: isLoading,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditListingGroupSettings,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditListingGroupSettings,
      });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.BulkEditListingGroupSettings,
        onClick: openDialog,
        disabled: !onlyListingGroupsSelected,
      },
    ];
  }, [onlyListingGroupsSelected, openDialog]);

  return {
    dropDown: (
      <LaunchBulkEditListingGroupSettings
        key={`LaunchBulkEditListingGroupSettings`}
        onClick={openDialog}
        onlyListingGroupsSelected={onlyListingGroupsSelected}
      />
    ),
    actionItems,
  };
};
